/**
 * Evo Tab Detection Utility
 * 
 * Provides a shared utility for detecting whether the app is running inside
 * an Evo workspace native tab or in standalone mode.
 */

/**
 * Detect if the app is running inside an Evo native tab
 * 
 * When running inside an Evo tab, the app receives context from the parent frame
 * and should use useWidgetIdentity instead of useIdentity.
 * 
 * @returns true if running inside an Evo native tab
 */
export const isRunningInEvoTab = (): boolean => {
  try {
    // Check if we're in an iframe (Evo tabs run in iframes)
    const inIframe = window.self !== window.top;
    
    // Check for Evo-specific URL parameters or context
    const urlParams = new URLSearchParams(window.location.search);
    const hasEvoContext = urlParams.has('evoContext') || urlParams.has('widgetId');
    
    // Check for Evo-specific postMessage handlers (set by Evo framework)
    const hasEvoFramework = typeof (window as unknown as { WidgetFramework?: unknown }).WidgetFramework !== 'undefined';
    
    return inIframe && (hasEvoContext || hasEvoFramework);
  } catch {
    // If we can't access window.top due to cross-origin restrictions,
    // we're likely in an iframe
    return true;
  }
};

/**
 * Check if the app is running in standalone mode (outside Evo tab)
 * 
 * @returns true if running in standalone mode
 */
export const isRunningStandalone = (): boolean => {
  return !isRunningInEvoTab();
};
